import React, { useRef } from 'react'
import type { CampoTemplate } from '../services/rapportinoTemplates'

interface Props {
  campo: CampoTemplate
  valore: any
  onChange: (valore: any) => void
  disabled?: boolean
}

export default function CampoRapportino({ campo, valore, onChange, disabled }: Props) {
  const { tipo, etichetta, obbligatorio, placeholder, opzioni, max_foto, larghezza, scelte_multiple } = campo

  if (tipo === 'separatore') {
    return (
      <div className="campo-wrapper separatore full">
        <div className="campo-separatore">
          <hr />
          {etichetta && <span>{etichetta}</span>}
        </div>
      </div>
    )
  }

  return (
    <div className={`campo-wrapper ${larghezza}`}>
      <label className="campo-label">
        {etichetta}
        {obbligatorio && <span className="campo-req">*</span>}
      </label>
      {tipo === 'testo' && (
        <input type="text" className="campo-input" value={valore ?? ''} disabled={disabled}
          placeholder={placeholder} onChange={e => onChange(e.target.value)} />
      )}
      {tipo === 'textarea' && (
        <textarea className="campo-input" rows={3} value={valore ?? ''} disabled={disabled}
          placeholder={placeholder} onChange={e => onChange(e.target.value)} />
      )}
      {tipo === 'numero' && (
        <input type="number" className="campo-input" value={valore ?? ''} disabled={disabled}
          placeholder={placeholder} onChange={e => onChange(e.target.value)} />
      )}
      {tipo === 'data' && (
        <input type="date" className="campo-input" value={valore ?? ''} disabled={disabled}
          onChange={e => onChange(e.target.value)} />
      )}
      {tipo === 'ora' && (
        <input type="time" className="campo-input" value={valore ?? ''} disabled={disabled}
          onChange={e => onChange(e.target.value)} />
      )}
      {tipo === 'checkbox' && (
        <div className="campo-yesno">
          <button type="button" disabled={disabled}
            className={`campo-yesno-btn yes${valore === true ? ' active' : ''}`}
            onClick={() => !disabled && onChange(valore === true ? null : true)}>
            ✓ Sì
          </button>
          <button type="button" disabled={disabled}
            className={`campo-yesno-btn no${valore === false ? ' active' : ''}`}
            onClick={() => !disabled && onChange(valore === false ? null : false)}>
            ✗ No
          </button>
        </div>
      )}
      {tipo === 'select' && !scelte_multiple && (
        <select className="campo-input" value={valore ?? ''} disabled={disabled}
          onChange={e => onChange(e.target.value)}>
          <option value="">— Seleziona —</option>
          {(opzioni ?? []).map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      )}
      {tipo === 'select' && scelte_multiple && (
        <div className="campo-checkbox-group">
          {(opzioni ?? []).map(o => {
            const sel: string[] = Array.isArray(valore) ? valore : []
            return (
              <label key={o} className="campo-checkbox-item">
                <input type="checkbox" checked={sel.includes(o)} disabled={disabled}
                  onChange={e => {
                    const next = e.target.checked ? [...sel, o] : sel.filter(v => v !== o)
                    onChange(next)
                  }} />
                {o}
              </label>
            )
          })}
        </div>
      )}
      {tipo === 'foto' && (
        <FotoCampo maxFoto={max_foto ?? 4} files={Array.isArray(valore) ? valore : []}
          onChange={onChange} disabled={disabled} />
      )}
      {tipo === 'firma' && (
        <FirmaCampo blob={valore} onChange={onChange} disabled={disabled} />
      )}
      {tipo === 'materiali' && (
        <div className="campo-materiali-placeholder">
          <span>📦 Sezione materiali — gestita nel passo Materiali</span>
        </div>
      )}
    </div>
  )
}

// ── FOTO ──────────────────────────────────────────────────────────────────────
function FotoCampo({ maxFoto, files, onChange, disabled }:
  { maxFoto: number; files: File[]; onChange: (f: File[]) => void; disabled?: boolean }) {
  const handleAdd = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(e.target.files ?? [])
    onChange([...files, ...newFiles].slice(0, maxFoto))
    e.target.value = ''
  }
  return (
    <div className="foto-campo">
      <div className="foto-grid">
        {files.map((f, idx) => (
          <div key={idx} className="foto-thumb">
            <img src={URL.createObjectURL(f)} alt={`foto ${idx + 1}`} />
            {!disabled && (
              <button type="button" className="foto-remove"
                onClick={() => onChange(files.filter((_, i) => i !== idx))}>×</button>
            )}
          </div>
        ))}
        {files.length < maxFoto && !disabled && (
          <label className="foto-add">
            <input type="file" accept="image/*" capture="environment" multiple
              onChange={handleAdd} style={{ display: 'none' }} />
            <span>📷 +</span>
            <span style={{ fontSize: 11 }}>{files.length}/{maxFoto}</span>
          </label>
        )}
      </div>
    </div>
  )
}

// ── FIRMA ─────────────────────────────────────────────────────────────────────
function FirmaCampo({ blob, onChange, disabled }:
  { blob: Blob | null; onChange: (b: Blob | null) => void; disabled?: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const drawing = useRef(false)

  const getPos = (e: React.MouseEvent | React.TouchEvent, canvas: HTMLCanvasElement) => {
    const rect = canvas.getBoundingClientRect()
    const src = 'touches' in e ? e.touches[0] : e
    return { x: src.clientX - rect.left, y: src.clientY - rect.top }
  }

  const start = (e: React.MouseEvent | React.TouchEvent) => {
    if (disabled) return
    e.preventDefault()
    drawing.current = true
    const canvas = canvasRef.current!
    const ctx = canvas.getContext('2d')!
    const p = getPos(e, canvas)
    ctx.beginPath(); ctx.moveTo(p.x, p.y)
  }

  const move = (e: React.MouseEvent | React.TouchEvent) => {
    if (!drawing.current || disabled) return
    e.preventDefault()
    const canvas = canvasRef.current!
    const ctx = canvas.getContext('2d')!
    const p = getPos(e, canvas)
    ctx.lineWidth = 2; ctx.strokeStyle = '#000'
    ctx.lineTo(p.x, p.y); ctx.stroke()
  }

  const end = () => {
    if (!drawing.current) return
    drawing.current = false
    canvasRef.current!.toBlob(b => { if (b) onChange(b) }, 'image/png')
  }

  const pulisci = () => {
    const canvas = canvasRef.current!
    canvas.getContext('2d')!.clearRect(0, 0, canvas.width, canvas.height)
    onChange(null)
  }

  return (
    <div className="firma-campo">
      <canvas ref={canvasRef} width={320} height={120}
        className={`firma-canvas${disabled ? ' disabled' : ''}`}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
      <div className="firma-actions">
        {blob && <span className="firma-ok">✓ Firmato</span>}
        {!disabled && (
          <button type="button" onClick={pulisci} className="firma-pulisci">Pulisci</button>
        )}
      </div>
    </div>
  )
}
